import { SearchOutlined } from "@ant-design/icons";
import type { TableProps } from 'antd';
import dayjs from "dayjs";
import { Button, Col, ConfigProvider, DatePicker, Flex, Input, Layout, Row, Skeleton, Table, notification, Upload, Modal } from "antd";
import { SorterResult } from "antd/es/table/interface";
import { useEffect, useState } from "react";
import { LoaderSpinner } from "../../../components/common/Loader";
import { getCobDenialPaymentFiles, uploadCobDenialPaymentFile } from "../../../service/httpsCalls";
import { createTableConfig } from "../tableConfig";
import { UploadOutlined } from '@ant-design/icons';

const { RangePicker } = DatePicker;

type NotificationType = 'success' | 'info' | 'warning' | 'error';
export default function CobDenialInvoicePaymentFiles() {

    const [ api, contextHolder ] = notification.useNotification();

    const [ tableColumns, setTableColumns ] = useState([]);
    const [ fileName, setfileName ] = useState<string | null>();
    const [ startDate, setStartDate ] = useState<string | any>('');
    const [ endDate, setEndDate ] = useState<string | any>('');
    const [ fileInfoList, setfileInfoList ] = useState([]);
    const [ loading, setLoading ] = useState(false);
    const [ pageSize, setPageSize ] = useState(0);
    const [ currentPage, setCurrentPage ] = useState(1);
    const [ totalPages, setTotalPages ] = useState(1);
    const [ sortedInfo, setSortedInfo ] = useState<SorterResult<any>>({});
    const [ menuVisible, setMenuVisible ] = useState(false);
    const [ showTable, setShowTable ] = useState(false);
    const [ filterLoader ] = useState(false);
    const [ fileUploadForm, setFileUploadForm ] = useState({
        file: null,
        show: false,
        loader: false,
        fileList: []
    })
    const [ clientId ] = useState(10);//10=Lacare
    const [ invoiceTypeId ] = useState(2); // 2= 837p Office Ally
    const openNotificationWithIcon = (type: NotificationType, message: string, description: string) => {
        api[ type ]({
            message: message,
            description: description
        });
    };

    function resetFilters() {
        setStartDate('');
        setEndDate('');
        setfileName(null);
    }

    function getfileInfoList() {
        setLoading(true);
        setfileInfoList([]);
        getCobDenialPaymentFiles({
            "pageIndex": currentPage,
            "pageSize": pageSize,
            "sortField": sortedInfo.order ? sortedInfo.field : null,
            "sortDescending": sortedInfo.order == 'descend',
            "clientId": clientId,
            "invoiceTypeId": invoiceTypeId,
            "fromUploadedDate": startDate && startDate != '' ? dayjs(startDate).format('YYYY-MM-DD') : null,
            "toUploadedDate": endDate && endDate != '' ? dayjs(endDate).format('YYYY-MM-DD') : null,
            "fileName": fileName ?? null

        }, (res: any) => {
            if (res !== 'error') {
                !showTable && setShowTable(true);

                if (res.columnConfigs && !tableColumns.length) {
                    let cols = createTableConfig(res.columnConfigs);

                    setTableColumns(cols);
                }

                res.columnConfigs.map((entry: any) => ({ item: entry.title, checked: !entry.hidden }))
                setfileInfoList(res.items);
                setLoading(false)
                setTotalPages(Math.ceil(res.totalRecords / (pageSize / pageSize)));
            } else {
                setfileInfoList([]);
                setLoading(false)
                setTotalPages(1);
            }

        })
    }
    function search() {

        if (pageSize == 0) {
            setPageSize(10);
        } else {
            currentPage == 1 ? getfileInfoList() : setCurrentPage(1);
        }
    }

    const [tableScrollY, setTableScrollY] = useState<number>();

    useEffect(() => {
        setPageSize(10);
        document.title = "Payment Files";

        const compute = () => {
            const height = window.innerHeight - 520;
            setTableScrollY(height > 0 ? height : undefined);
        };

        compute();
        window.addEventListener("resize", compute);
        return () => window.removeEventListener("resize", compute);
    }, []);

    useEffect(() => {
        if (pageSize != 0)
            getfileInfoList();
    }, [ currentPage, pageSize, sortedInfo ]);

    const handleTableChange: TableProps<any>[ 'onChange' ] = (pagination, filters, sorter, extra) => {
        setCurrentPage(pagination.current ?? 1);
        setPageSize(pagination.pageSize ?? 10);
        setTotalPages(pagination.total ?? 100);
        setSortedInfo(sorter as SorterResult<any>);
    };

    function fileUploaded({ file, fileList }) {
        if (file?.status == "removed") {
            setFileUploadForm({
                ...fileUploadForm,
                file: null,
                fileList: []
            });
            return;
        }
        const allowedTypes = [
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx

        ];

        if (!allowedTypes.includes(file.type)) {
            openNotificationWithIcon("warning", `${file.name} is not an allowed file type.`, "");
            setFileUploadForm({
                ...fileUploadForm,
                fileList: []
            });
            return Upload.LIST_IGNORE;
        } else {
            setFileUploadForm({
                ...fileUploadForm,
                file: file.originFileObj || file,
                fileList: fileList
            });
        }
    }

    function saveFile() {
        if (!fileUploadForm.file) {
            openNotificationWithIcon("warning", "No file selected. Please choose a file to upload.", "");
            return;
        }
        setFileUploadForm({ ...fileUploadForm, loader: true })
        uploadCobDenialPaymentFile({ clientId: clientId, invoiceTypeId: invoiceTypeId, file: fileUploadForm.file }, (res) => {
            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[ 0 ].description : 'Something went wrong!!!'
                openNotificationWithIcon("warning", errorMsg, "");
                setFileUploadForm({ ...fileUploadForm, loader: false })
            } else {
                openNotificationWithIcon("success", "File uploaded!!", "");
                setFileUploadForm({ ...fileUploadForm, show: false, loader: false, file: null, fileList: [] })
                currentPage == 1 ? getfileInfoList() : setCurrentPage(1);
            }

        })
    }

    return <ConfigProvider theme={{
        components: {
            Layout: { bodyBg: "#eff1f5", siderBg: "#fff" }
        }
    }} >
        {contextHolder}
        <Layout style={{
            padding: '20px',
            marginBottom: '5px',
            marginTop: '5px',
        }}>


            <Modal title="Upload File" open={fileUploadForm.show}
                afterClose={() => setFileUploadForm({ ...fileUploadForm, show: false, file: null, fileList: [] })}
                onCancel={() => setFileUploadForm({ ...fileUploadForm, show: false, file: null, fileList: [] })}
                onOk={() => saveFile()}
                confirmLoading={fileUploadForm.loader}
                width={500}
                maskClosable={false}
                className="customModal"
            >
                <div>
                    <Row>
                        <Col span={6}>
                            File
                        </Col>
                        <Col span={18}>
                            <Upload maxCount={1} fileList={fileUploadForm.fileList} onChange={fileUploaded} beforeUpload={() => false} accept=".xlsx,.xls">
                                <Button style={{ width: '100%' }}>
                                    <UploadOutlined /> Click to Upload
                                </Button>
                            </Upload>
                        </Col>
                    </Row>

                </div>
            </Modal>

            <h3 style={{ textTransform: 'uppercase', marginTop: '0px' }}>Payment Files</h3>
            <Flex justify='space-between' style={{ padding: "1rem", alignItems: "center", background: '#fff' }}>
                <label><SearchOutlined />&nbsp;Search</label>
                <Button onClick={e => setFileUploadForm({ ...fileUploadForm, show: true, loader: false })} style={{ marginLeft: '5px' }} type="primary" icon={<UploadOutlined />}>
                    Upload File
                </Button>

            </Flex>
            <Skeleton loading={filterLoader} active>
                <Row type="flex" style={{ padding: '1rem', paddingTop: "0px", background: '#fff' }}>
                    <Col span={6}>
                        <Input value={fileName ?? ''}
                            placeholder="File Name"
                            style={{ width: '90%' }}
                            allowClear onChange={(e) => setfileName(e?.target?.value)} />
                    </Col>

                    <Col span={12}>
                        <RangePicker
                            placeholder={[ "From Uploaded Date", "To Uploaded Date" ]}
                            style={{ width: '100%' }}
                            format="MM/DD/YYYY"
                            value={[ startDate, endDate ]}
                            onChange={(dates: any) => {
                                if (dates) {
                                    setStartDate(dates[ 0 ]);
                                    setEndDate(dates[ 1 ]);
                                } else {
                                    setStartDate('');
                                    setEndDate('');
                                }
                            }}
                        />
                    </Col>

                </Row>

                <Flex justify='end' style={{ marginBottom: "2rem", padding: "1rem", paddingTop: "0px", background: '#fff' }} gap='small'>
                    <Button onClick={search} loading={loading} style={{ marginLeft: '5px' }} type="primary">
                        Search
                    </Button>
                    <Button onClick={resetFilters} style={{ backgroundColor: '#ffffff', borderColor: '#d9d9d9', marginLeft: '5px', color: 'rgba(0, 0, 0, 0.88)' }} type="primary">
                        Reset
                    </Button>
                </Flex>
            </Skeleton>
            {showTable && <Table style={{ background: "#fff" }}
                scroll={{
                    y: fileInfoList?.length > 0 ? tableScrollY : undefined,
                }}
                dataSource={fileInfoList}
                pagination={{
                    defaultCurrent: 1,
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalPages,
                    showTotal: (total, range) => `${range[ 0 ] || 0}-${range[ 1 ] || 1} of ${total} items`,
                    showSizeChanger: true,
                }}
                columns={tableColumns}
                onChange={handleTableChange}
                size="small"
                rowKey="fileName"
                loading={{ indicator: <LoaderSpinner size={24} topMargin="10px" />, spinning: loading }}
                onHeaderRow={(columns, index) => {
                    return {
                        onContextMenu: (event) => {
                            event.preventDefault();
                            if (!menuVisible) {
                                document.addEventListener(`click`, function onClickOutside() {
                                    setMenuVisible(false);
                                    document.removeEventListener(`click`, onClickOutside);
                                });
                            }
                            setMenuVisible(true);
                        }
                    };
                }}
            />

            }
        </Layout>
    </ConfigProvider>
}